import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { Routes } from "@lifeat/shared/system/RouteConstants";
import LifeAtLogo from "@resources/logos/LifeAt-Logo-Large.svg";
import { logError } from "@service/ConsoleLoggerService";
import { callMonolith } from "@service/ServerlessFunctionsService";
import { buildDeepLink } from "@util/DeepLinkUtil";
import { buildRoute } from "@util/RouteUtil";
import LoadingView from "../../features/virtual-space/components/SpaceView/LoadingView/LoadingView";

type State = "loading" | "error" | "complete" | "redirected";

const Page = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100vh;
    background: ${({ theme }) => theme.backgroundPrimary};
    gap: 20px;
`;

const Logo = styled(LifeAtLogo)`
    fill: ${({ theme }) => theme.textColorPrimary};
`;

export const OAuthOutlookCalendarCallbackPage = () => {
    const [state, setState] = useState<State>("loading");
    const [errorCountdown, setErrorCountdown] = useState(10);
    const history = useHistory();

    async function handleOAuthCallback(): Promise<State> {
        try {
            const receivedParams = new URLSearchParams(window.location.search);
            const code = receivedParams.get("code");
            const state = JSON.parse(receivedParams.get("state"));
            const { shouldGoBackToDesktop } = state;

            if (shouldGoBackToDesktop) {
                const desktopUrl = buildDeepLink(
                    `/oauth/outlook-calendar/callback?${new URLSearchParams({ code, state: JSON.stringify({ ...state, shouldGoBackToDesktop: false }) })}`,
                );
                window.location.href = desktopUrl;
                return "redirected";
            }

            await callMonolith("outlook-connect-calendar", { code });
            return "complete";
        } catch (e) {
            logError(String(e));
            return "error";
        }
    }

    useEffect(() => {
        handleOAuthCallback().then(setState);
    }, []);

    useEffect(() => {
        if (state === "complete") {
            history.push(buildRoute(Routes.App) + "?cal=true");
        }
    }, [state, history]);

    useEffect(() => {
        if (state === "error") {
            const countdownInterval = setInterval(() => {
                setErrorCountdown((prev) => {
                    const next = prev - 1;
                    if (next === 0) {
                        clearInterval(countdownInterval);
                        history.push(buildRoute(Routes.App));
                    }
                    return next;
                });
            }, 1000);

            return () => clearInterval(countdownInterval);
        }
    }, [state, history]);

    return (
        <>
            {state === "loading" && <LoadingView isAppLoading />}
            {state === "error" && (
                <p>Failed to connect Outlook Calendar. Redirecting back in {errorCountdown} seconds</p>
            )}
            {state === "redirected" && (
                <Page>
                    <Logo />
                    <p>Redirecting to desktop app...</p>
                </Page>
            )}
        </>
    );
};
